// SignIn.tsx — development sign-in.
//
// WHAT THIS IS, PLAINLY: authentication is bypassed (issue #17), so there is no real login. This
// screen lets you choose which seeded identity the browser presents, and the server honours that
// choice ONLY while TOWNFORGE_AUTH_BYPASS is true. With real auth on, `/dev/users` returns 404 and
// this screen never appears.
//
// It exists because the alternative was editing .env and recreating a container to change user,
// which makes a demo unusable — and because a fresh install once handed over a box where the
// configured identity matched no seeded user, so every screen reported "no TownForge account for
// this identity" while the stack was perfectly healthy.
//
// It is labelled as development sign-in wherever it appears. A screen that looks like a real login
// but is not would be the most misleading thing in the product.
import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { api, ApiError, setDevUser, type DevUser } from '../lib/api'
import { ROLE_LABELS } from '../lib/format'

export default function SignIn({ onSignedIn }: { onSignedIn?: () => void }) {
  const { me } = useAuth()
  const [users, setUsers] = useState<DevUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.devUsers().then(d => setUsers(d.users))
      .catch((e: unknown) => setError(
        e instanceof ApiError && e.status === 404
          ? 'Authentication is enabled on this server, so development sign-in is unavailable.'
          : e instanceof ApiError ? e.detail : String(e)))
  }, [])

  function choose(id: string) {
    setDevUser(id)
    if (onSignedIn) { onSignedIn(); return }
    // A FULL RELOAD, not a router navigation. Identity is loaded once at the top of the tree and
    // every capability gate in the app is derived from it; re-running the router with the old
    // identity still in memory would leave one role's menu over another role's data. Switching
    // who you are is a new session, so it starts one.
    location.href = '/'
  }

  if (error) return <div className="page-state page-state-error">{error}</div>
  if (!users) return <div className="page-state">Loading…</div>

  // Grouped by organisation so the portal consequence is visible while choosing: picking an
  // Arrears Manager lands in AMS and nothing else.
  const byOrg = new Map<string, DevUser[]>()
  for (const u of users) {
    const k = u.orgName ?? 'Platform'
    byOrg.set(k, [...(byOrg.get(k) ?? []), u])
  }

  return (
    <div className="signin">
      <h1>TownForge</h1>
      <p className="muted">
        <b>Development sign-in.</b> Authentication is not switched on for this server, so choose the
        identity to work as. What you can see and do follows from the role.
      </p>
      {/* Who you are NOW. Without it, a picker that lists seven names gives no clue which one you
          are already using, and "switch user" is hard to tell apart from a page that did nothing. */}
      {me && (
        <p className="signin-now">
          Currently signed in as <b>{me.user.name}</b>
          {me.designation ? ` · ${me.designation}` : ''}. Choosing a different person below
          switches to them.
        </p>
      )}

      {[...byOrg.entries()].map(([orgName, list]) => (
        <section key={orgName} className="card">
          <h3>{orgName}</h3>
          <ul className="signin-list">
            {list.map(u => (
              <li key={u.id}>
                <button className={me?.user.id === u.id ? 'on' : ''}
                        onClick={() => choose(u.id)}>
                  <b>{u.name}</b>
                  <span className="muted sm">
                    {u.designation ?? ROLE_LABELS[u.role ?? ''] ?? u.role ?? 'No role'}
                  </span>
                  {u.isSuperuser && <span className="tag">superuser</span>}
                  {me?.user.id === u.id && <span className="tag blue">you</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
